import { useThree } from "@react-three/fiber";
import { forwardRef, useImperativeHandle } from "react";
import * as THREE from 'three';

export type ModelData = {
    xPosition?: number;
    yPosition?: number;
    zPosition?: number;
    xRotation?: number;
    yRotation?: number;
    zRotation?: number;
    xScale?: number;
    yScale?: number;
    zScale?: number;
};

export type ModelSpaceExposerRef = {
    getModifiedModel: (uuid: string) => THREE.Object3D | THREE.Object3D[] | undefined;
    getModel: (uuid: string) => THREE.Object3D | THREE.Object3D[] | undefined;
    deleteByUUID: (uuid: string) => boolean;
    renameModel: (uuid: string, name: string) => void;
    getModelData: (uuid: string) => ModelData | undefined;
    isPOI: (uuid: string) => boolean;
    setModelData: (uuid: string, data: ModelData) => void;
    changeModelvisibility: (uuid: string, visible: boolean) => void;
    getUUIDsOfAModelAndItsChildren: (uuid: string) => Set<string>;
    setModelMaterial: (uuid: string, material: THREE.Material) => void;
};

export const ModelSpaceRefExposer = forwardRef<ModelSpaceExposerRef>((props, ref) => {
    const scene = useThree(state => state.scene);
    
    useImperativeHandle(ref, () => ({
        // gyökérmodell lekérése
        getModifiedModel: (uuid: string) => {
            const model = scene.getObjectByProperty('uuid', uuid);

            if (model && model.children && model.children.length > 0 && model.children[0].children) return model.children[0].children;

            return undefined;
        },
        // bármilyen modell lekérése UUID alapján
        getModel: (uuid: string) => {
            return scene.getObjectByProperty('uuid', uuid);
        },
        deleteByUUID: (uuid: string) => {
            const object = scene.getObjectByProperty('uuid', uuid);
            if (object) {
                if (object.userData?.isPOI) return false;

                if (object.parent) {
                    object.parent.remove(object);
                    return true;
                }
            }
            
            return false;
        },
        renameModel: (uuid: string, name: string) => {
            const object = scene.getObjectByProperty('uuid', uuid);

            if (object) object.name = name;
        },
        getModelData: (uuid: string) => {
            const object = scene.getObjectByProperty('uuid', uuid);

            if (!object) return;
            
            return {
                xPosition: object.position.x,
                yPosition: object.position.y,
                zPosition: object.position.z,
                xRotation: object.rotation.x,
                yRotation: object.rotation.y,
                zRotation: object.rotation.z,
                xScale: object.scale.x,
                yScale: object.scale.y,
                zScale: object.scale.z
            };
        },
        isPOI: (uuid: string) => {
            const model = scene.getObjectByProperty('uuid', uuid);
            if (model && model.userData?.isPOI) return true;
            return false;
        },
        setModelData: (uuid: string, data: ModelData) => {
            const object = scene.getObjectByProperty('uuid', uuid);

            if (!object) return;

            if (data.xPosition !== undefined && data.xPosition !== null) object.position.x = data.xPosition;
            if (data.yPosition !== undefined && data.yPosition !== null) object.position.y = data.yPosition;
            if (data.zPosition !== undefined && data.zPosition !== null) object.position.z = data.zPosition;
            if (data.xRotation !== undefined && data.xRotation !== null) object.rotation.x = data.xRotation;
            if (data.yRotation !== undefined && data.yRotation !== null) object.rotation.y = data.yRotation;
            if (data.zRotation !== undefined && data.zRotation !== null) object.rotation.z = data.zRotation;
            if (data.xScale !== undefined && data.xScale !== null) object.scale.x = data.xScale;
            if (data.yScale !== undefined && data.yScale !== null) object.scale.y = data.yScale;
            if (data.zScale !== undefined && data.zScale !== null) object.scale.z = data.zScale;
        },
        changeModelvisibility: (uuid: string, visible: boolean) => {
            const object = scene.getObjectByProperty('uuid', uuid);
            if (object) object.visible = visible;
        },
        getUUIDsOfAModelAndItsChildren: (uuid: string) => {
            const object = scene.getObjectByProperty('uuid', uuid);

            const uuids = new Set<string>();
            if (object) {
                object.traverse(object => {
                    uuids.add(object.uuid);
                })
            }

            return uuids;
        },
        setModelMaterial: (uuid: string, material: THREE.Material) => {
            const object = scene.getObjectByProperty('uuid', uuid);

            if (object && object.material) object.material = material;
        }
    }), [scene]);

    return null;
});